import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';

import { actionCreators } from 'react/api/actions/crud';
import actionsSurvey from 'react/api/actions/survey';
import { BaseCrud } from 'react/containers/layout/Crud';
import ApplicationRulesSection from 'react/components/Survey/ApplicationRulesSection';

import { default as generalSection } from 'react/components/Survey/GeneralSection';
import { default as sectionsSection } from 'react/components/Survey/SectionsSection';
import { default as actionsSection } from 'react/components/Survey/ActionsSection';

const getSafeSurveySections = (surveySections) => {
  surveySections.forEach(section => {
    const safeSurveyQuestions = [];

    section.surveyQuestions.forEach(question => {
      if (!safeSurveyQuestions.some(item => item.id && (item.id === question.id)))
        safeSurveyQuestions.push(question);
    });

    section.surveyQuestions = safeSurveyQuestions;
  });

  return surveySections;
};

const filterRemovedSurveyOrganizations = (surveyOrganizations) => {
  return surveyOrganizations.filter(surveyOrganization => !surveyOrganization.removed);
}

const beforeSubmit = (values, id) => {
  const { description, surveySections } = values;
  const adjustedValues = values;
  adjustedValues.title = description;
  adjustedValues.surveyOrganizations = filterRemovedSurveyOrganizations(values.surveyOrganizations);
  adjustedValues.organization = null;

  if (!Greenmile.Survey.Form.checkMandatoryFields()) {
    return null;
  }

  if (id) {
    adjustedValues.id = parseInt(id, 10);
  }

  const dataToSaveIntoRedux = Greenmile.Survey.SurveyApplyRule.prepareDataToSend();

  if (!dataToSaveIntoRedux || dataToSaveIntoRedux.length === 0) {
    return null;
  } else {
    adjustedValues.surveyApplyRules = dataToSaveIntoRedux;
  }

  if (!surveySections) {
    adjustedValues.surveySections = Greenmile.Survey.Builder.buildSurvey();
  } else {
    adjustedValues.surveySections = Greenmile.Survey.Builder.buildSurveyForUpdate();
  }

  adjustedValues.surveySections = getSafeSurveySections(adjustedValues.surveySections);

  return adjustedValues;
};

const setCustomPlayloadRequest = ({ id }) => ({
  criteriaChain: [
    {
      and: [
        { attr: 'id', eq: id },
        {
          attr: 'surveySections.surveyQuestions.parentQuestion.id',
          isNull: true
        }
      ]
    }
  ],
  sort: [
    { attr: 'surveySections.sectionOrder', type: 'ASC' },
    { attr: 'surveySections.surveyQuestions.questionOrder', type: 'ASC' },
    {
      attr: 'surveySections.surveyQuestions.subQuestions.questionOrder',
      type: 'ASC'
    }
  ]
});

const setCustomActions = ({ isGrid }) => {
  if (isGrid) {
    return [{
      customLabel: 'Survey.GridMenu.releaseSurvey',
      buttonOrder: 1,
      name: 'btn_release_survey_menu'
    },
    {
      customLabel: 'Survey.GridMenu.closeSurvey',
      buttonOrder: 1,
      name: 'btn_close_survey_menu'
    }];
  }
  return null;
};

const setReleasedAndClosedDates = ({ releasedDate, closedDate }) => {
  (releasedDate || closedDate)
    && Greenmile.Survey.Form.setReleasedAndClosedDates(releasedDate, closedDate);
};

const setApplyRulesSection = ({ surveyApplyRules }) => {
  surveyApplyRules
    && !Greenmile.Survey.SurveyApplyRule.applyRuleBuilded
    && Greenmile.Survey.Form.addApplyRules({ surveyApplyRules });
};

const setSectionsSection = ({ id, surveySections }) => {
  Greenmile.Survey.Form.linkedQuestions = Greenmile.Survey.Form.linkedQuestions || [];
  surveySections
    && id
    && !Greenmile.Survey.Builder.surveyBuilded
    && Greenmile.Survey.Form.addSections(surveySections);
};

const setActionsSection = () => {
  !Greenmile.Survey.Builder.surveyBuilded
    && Greenmile.Survey.Form.addActions();
};

const resetData = () => {
  Greenmile.Survey.Builder.releasedDate = null;
  Greenmile.Survey.Builder.closedDate = null;

  Greenmile.Survey.SurveyApplyRule.clearApplyRuleData();

  Greenmile.Survey.Data.clearSurveyData();
  Greenmile.Survey.Form.linkedQuestions = [];
  Greenmile.Survey.Builder.surveyBuilded = false;
};

const SurveyCrud = (props) => {
  const { surveyApplyRules, surveySections, isGrid } = props;

  useEffect(() => {
    if (!isGrid) {
      setReleasedAndClosedDates(props);
      setApplyRulesSection(props);
      setSectionsSection(props);
      setActionsSection();
      Greenmile.Survey.Form.disableButtons();
    } else {
      resetData();
    }
  }, [surveyApplyRules, surveySections, isGrid]);

  return (
    <BaseCrud
      updateMethod="PUT"
      tabName="topMenu.survey"
      beforeSubmit={beforeSubmit}
      customCriteriaChain={setCustomPlayloadRequest(props)}
      customActions={setCustomActions(props)}
      {...props}
    >

      {generalSection(props)}
      <ApplicationRulesSection {...props} />
      {sectionsSection(props)}
      {actionsSection(props)}
    </BaseCrud>
  );
};

SurveyCrud.propTypes = {
  id: PropTypes.string,
  isGrid: PropTypes.bool.isRequired,
  releasedDate: PropTypes.string,
  closedDate: PropTypes.string,
  surveySections: PropTypes.array,
  surveyApplyRules: PropTypes.array
};

const mapDispatchToProps = {
  invalidSubmitCustomAction: actionCreators.invalidSubmitCustom,
  toggleApplicationRulesModalAction: actionsSurvey.actionCreators.toggleApplicationRulesModal
};

const selector = formValueSelector('Survey');
export default connect(
  (state, ownProps) => ({
    organization: selector(state, 'organization'),
    surveyOrganizations: selector(state, 'surveyOrganizations'),
    surveyApplyRules: selector(state, 'surveyApplyRules'),
    surveySections: selector(state, 'surveySections'),
    releasedDate: selector(state, 'releasedDate'),
    closedDate: selector(state, 'closedDate'),
    invalidSubmitCustom: state.crud.getIn([ownProps.entity, 'invalidSubmitCustom'], false),
    loadingInitialValues: state.crud.getIn([ownProps.entity, 'loadingInitialValues']),
    organizationsHierarchy: Monet.Session.ApplicationLoad.organizationsHierarchy,
  }),
  mapDispatchToProps
)(SurveyCrud);